/**
 * Well-known role URIs for the Dataminds platform.
 * These should match the roles defined in Keycloak and the backend.
 */
export const DatamindsRoles = {
    ADMIN: 'Organization.Chat.Admin'
} as const;

export const ChatAppRoles = {
    BASIC: 'Organization.Chat.Basic.Access',
    ADVANCED: 'Organization.Chat.Advanced.Access'
} as const;

export type DatamindsRole =
    (typeof DatamindsRoles)[keyof typeof DatamindsRoles];

/**
 * Helper function to check if a user has a specific role.
 */
export function hasRole(
    roles: string[] | undefined | null,
    role: DatamindsRole
): boolean {
    return roles?.includes(role) ?? false;
}

/**
 * Helper function to check if a user has the admin role.
 */
export function isAdmin(roles: string[] | undefined | null): boolean {
    return hasRole(roles, DatamindsRoles.ADMIN);
}

const forcedBasicInDevelopment = () => {
    try {
        return (
            import.meta.env.DEV &&
            localStorage.getItem('forceBasicUser') === 'true'
        );
    } catch {
        return false;
    }
};

/**
 * A basic user only sees Organization artifacts and Scheduled jobs, and
 * cannot create jobs. Anyone who also has the advanced role keeps the wider
 * access. Admin is an extra role on top and does not change the level.
 */
export function isBasicUser(
    metadata: { organization_roles?: string[] } | undefined | null
): boolean {
    if (forcedBasicInDevelopment()) return true;
    const roles = metadata?.organization_roles ?? [];
    return (
        roles.includes(ChatAppRoles.BASIC) &&
        !roles.includes(ChatAppRoles.ADVANCED)
    );
}
